"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.acoGroupPerformanceSchema = exports.groupPerformanceRowSchema = exports.groupPerformanceFigureSchema = exports.groupPerformanceQuerySchema = exports.performanceGroupDetailSchema = exports.performanceGroupSchema = exports.groupPracticeSchema = exports.performanceGroupQuerySchema = exports.performanceGroupParamsSchema = exports.performanceGroupsQuerySchema = void 0;
// Wire shapes of the performance-group routes (SPEC §7.2 Screen 1, §9 `/api/groups?aco` and
// `/api/groups/:id`; CR-001 §5.11; J-035), served at `/api/v1/performance-groups` (D-973). Numbers are
// raw (SPEC §9: "All responses carry numbers unformatted"); the web formats them with `format.ts`.
// Group, TIN and NPI values are organisation and clinician identifiers, never patient data.
const zod_1 = require("zod");
const organization_js_1 = require("../config/organization.js");
// The value list of `core.performance_group_npi`'s CHECK constraint (packages/schema/src/db/aco-org.ts).
const REGISTRATION_SOURCES = ["HMX_CONFIG", "AUTO_FROM_ENCOUNTERS", "ACO_MS", "MANUAL"];
// No `.meta({ id })` on a querystring or params schema: @fastify/swagger expands parameters inline.
// There is no `source` parameter: the score source stays internal to the repo (SPEC §7.0).
exports.performanceGroupsQuerySchema = zod_1.z.object({
    year: zod_1.z.coerce.number().int().min(2000).max(2100),
    /** One ACO's code; omitted means every ACO in the caller's scope (the selector's `All`). */
    aco: zod_1.z.string().min(1).max(64).optional(),
});
exports.performanceGroupParamsSchema = zod_1.z.object({
    groupId: zod_1.z.coerce.number().int().positive().max(2_147_483_647),
});
/**
 * J-268 (QB3 item 2): one ACO's code, for a group whose practices span ACOs: the group's figures within
 * that ACO. Omitted means the group's figures over every practice ("Both"), when the caller's scope
 * covers every ACO of the group.
 */
exports.performanceGroupQuerySchema = zod_1.z.object({
    aco: zod_1.z.string().min(1).max(64).optional(),
});
/** J-268: one practice of a performance group: its TIN, its configured name and its ACO. */
exports.groupPracticeSchema = zod_1.z
    .object({ tin: zod_1.z.string(), practiceName: zod_1.z.string().nullable(), acoCode: zod_1.z.string() })
    .meta({ id: "AcoGroupPractice" });
/** J-268: the ACO fields of a group: every ACO it reaches in scope, and which one its figures are for. */
const groupAcoFields = {
    /**
     * Every ACO the group has a practice in that the caller may see, by code. More than one: the group's
     * screens offer the ACO filter (Both, then each ACO).
     */
    acoCodes: zod_1.z.array(zod_1.z.string()),
    /** The ACO the figures are for, or null for the group's figures over all its practices ("Both"). */
    figuresAco: zod_1.z.string().nullable(),
};
exports.performanceGroupSchema = zod_1.z
    .object({
    groupId: zod_1.z.number().int(),
    performanceYear: zod_1.z.number().int(),
    groupName: zod_1.z.string(),
    groupType: organization_js_1.groupTypeSchema,
    displayOrder: zod_1.z.number().int().nullable(),
    visitAttributionMode: organization_js_1.visitAttributionModeSchema,
    apmEntityId: zod_1.z.string().nullable(),
    /** The group's ACO; J-268: for a group whose practices span ACOs, the first of `acoCodes`. */
    acoCode: zod_1.z.string(),
    ...groupAcoFields,
    platformName: zod_1.z.string().nullable(),
    /** `ref.pathway.pathway_type`, e.g. `APP`. */
    pathwayType: zod_1.z.string(),
    /** `ref.pathway.display_name`, e.g. `APP CQM Only`. */
    pathwayName: zod_1.z.string(),
    /** 100 × earned ÷ maximum, a projection (CR-001 §5.11); null when no measure is scored. */
    scoreEst: zod_1.z.number().nullable(),
    qualityEarned: zod_1.z.number(),
    qualityMax: zod_1.z.number(),
    /** Pathway measures counted toward the maximum: SCORED, BELOW_COMPLETENESS and NO_DATA. */
    measuresScored: zod_1.z.number().int(),
    piPoints: zod_1.z.number().nullable(),
    iaPoints: zod_1.z.number().nullable(),
    costPoints: zod_1.z.number().nullable(),
})
    .meta({ id: "AcoPerformanceGroup" });
const groupNpiSchema = zod_1.z.object({
    npi: zod_1.z.string(),
    tin: zod_1.z.string(),
    registrationSource: zod_1.z.enum(REGISTRATION_SOURCES),
});
/** One group with the TINs and NPIs Screen 1's Manage NPIs panel lists (SPEC §7.2, R-207). */
exports.performanceGroupDetailSchema = exports.performanceGroupSchema
    .extend({
    tins: zod_1.z.array(zod_1.z.string()),
    /** J-268: the group's practices in the caller's scope, each with its TIN, name and ACO. */
    practices: zod_1.z.array(exports.groupPracticeSchema),
    npis: zod_1.z.array(groupNpiSchema),
})
    .meta({ id: "AcoPerformanceGroupDetail" });
// J-202 (CR-005 item 16): Performance's group view, `GET /api/v1/acos/:acoCode/group-performance?year`.
exports.groupPerformanceQuerySchema = zod_1.z.object({
    year: zod_1.z.coerce.number().int().min(2000).max(2100),
});
/**
 * One group's figures for one measure, from `core.group_measure_score`: Screen 2's counts and its
 * performance rate, by the same rule. Counts over the group's patients, never a patient's value.
 */
exports.groupPerformanceFigureSchema = zod_1.z
    .object({
    measureKey: zod_1.z.number().int(),
    met: zod_1.z.number().int(),
    notMet: zod_1.z.number().int(),
    excluded: zod_1.z.number().int(),
    exceptions: zod_1.z.number().int(),
    /** `met`. */
    numerator: zod_1.z.number().int(),
    /** complete − exceptions, the divisor of Screen 2's performance rate. */
    denominator: zod_1.z.number().int(),
    /** 100 × met ÷ (complete − exceptions), as `calculate` stored it; null when the divisor is 0. */
    performanceRate: zod_1.z.number().nullable(),
})
    .meta({ id: "AcoGroupPerformanceFigure" });
/** One performance group's row: its quality points and a figure per measure it has a score for. */
exports.groupPerformanceRowSchema = zod_1.z
    .object({
    groupId: zod_1.z.number().int(),
    groupName: zod_1.z.string(),
    groupType: organization_js_1.groupTypeSchema,
    displayOrder: zod_1.z.number().int().nullable(),
    /** True for the ACO-wide (APM_ENTITY) group, which comes first. */
    isAcoWide: zod_1.z.boolean(),
    /** Screen 1's totals (`groupTotals`): earned of the maximum. */
    qualityEarned: zod_1.z.number(),
    qualityMax: zod_1.z.number(),
    /** J-268: within this ACO, for a group whose practices span ACOs. */
    figures: zod_1.z.array(exports.groupPerformanceFigureSchema),
    /** J-268: every ACO the group has a practice in that the caller may see, by code. */
    acoCodes: zod_1.z.array(zod_1.z.string()),
    /**
     * J-268: for a group whose practices span ACOs, its totals and figures over all its practices
     * ("Both"), when the caller may see every ACO of the group; else null.
     */
    combined: zod_1.z
        .object({
        qualityEarned: zod_1.z.number(),
        qualityMax: zod_1.z.number(),
        figures: zod_1.z.array(exports.groupPerformanceFigureSchema),
    })
        .nullable(),
})
    .meta({ id: "AcoGroupPerformanceRow" });
exports.acoGroupPerformanceSchema = zod_1.z
    .object({
    acoCode: zod_1.z.string(),
    performanceYear: zod_1.z.number().int(),
    /** The measures any group has a score for, in `ref.measure.display_order`. */
    measures: zod_1.z.array(zod_1.z.object({
        measureKey: zod_1.z.number().int(),
        measureNumber: zod_1.z.string(),
        displayNumber: zod_1.z.string(),
        /** The measures document's `short_label`, else the display number. */
        shortLabel: zod_1.z.string(),
        title: zod_1.z.string(),
        isInverse: zod_1.z.boolean(),
    })),
    /** The ACO-wide group first, then `display_order` (unset last), then the group name. */
    groups: zod_1.z.array(exports.groupPerformanceRowSchema),
})
    .meta({ id: "AcoGroupPerformance" });
//# sourceMappingURL=groups.js.map